import { useRequest } from "ahooks";
import { message } from "antd";
import powerBiService from "service/powerBi";
import type { PowerBiEmbedConfig } from "service/powerBi/types";

interface UseGetReportResult {
  loading: boolean;
  config: PowerBiEmbedConfig | null;
  refresh: () => void;
}

/**
 * Loads the Power BI embed configuration for the dashboard on mount.
 * When enabled is false (e.g. the user cannot access the dashboard) the request is
 * gated off via `ready` and never fires. Surfaces a user-facing error toast on failure.
 *
 * `refresh` re-runs the same request to fetch a fresh embed token, used when the
 * previously issued token has expired. Throttled (leading-only) so a proactive,
 * timer-driven refresh and a reactive, ClientError_TokenExpired-driven refresh
 * firing within the same few seconds only hit the backend once.
 *
 * retryCount covers transient failures on any of those calls (initial load,
 * proactive, or reactive) with exponential backoff (2s/4s/8s), so one network
 * blip doesn't silently drop the proactive refresh and leave the dashboard
 * waiting on the reactive fallback for that cycle.
 */
export const useGetReport = (enabled = true): UseGetReportResult => {
  const { loading, data, refresh } = useRequest(powerBiService.getEmbedConfig, {
    ready: enabled,
    throttleWait: 5000,
    throttleLeading: true,
    throttleTrailing: false,
    retryCount: 3,
    onError: () => {
      message.error("Failed to load the Power BI dashboard.");
    },
  });

  return { loading, config: data ?? null, refresh };
};
